'use client';

import Fuse from 'fuse.js';
import { type LucideIcon, type LucideProps } from 'lucide-react';
import {
  DynamicIcon,
  dynamicIconImports,
  type IconName,
} from 'lucide-react/dynamic';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';

import { cn } from '../../utils/cn';
import { Button } from '../button';
import { Input } from '../input';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import { Skeleton } from '../skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../tooltip';
import { type iconsData } from './icons-data';
import {
  VirtualizedIconList,
  type VirtualizedIconListHandle,
} from './virtualized-icon-list';

export type IconData = (typeof iconsData)[number];
export type IconList = IconData[];

/** Shown while Lucide loads the SVG (DynamicIcon is async); avoids empty cells in the grid. */
function PickerIconFallback() {
  return (
    <span
      className="block h-4 w-4 shrink-0 rounded-sm bg-muted-foreground/20 animate-pulse"
      aria-hidden
    />
  );
}

function useScrollFades({
  element,
  axis,
  threshold = 2,
  measureKey,
}: {
  element: HTMLElement | null;
  axis: 'x' | 'y';
  threshold?: number;
  measureKey?: string | number;
}) {
  const [showStart, setShowStart] = useState(false);
  const [showEnd, setShowEnd] = useState(false);

  useEffect(() => {
    if (!element) return;

    const update = () => {
      if (axis === 'x') {
        const max = element.scrollWidth - element.clientWidth;
        setShowStart(element.scrollLeft > threshold);
        setShowEnd(element.scrollLeft < max - threshold);
        return;
      }
      const max = element.scrollHeight - element.clientHeight;
      setShowStart(element.scrollTop > threshold);
      setShowEnd(element.scrollTop < max - threshold);
    };

    update();
    element.addEventListener('scroll', update, { passive: true });

    const ro = new ResizeObserver(update);
    ro.observe(element);

    return () => {
      element.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [element, axis, threshold, measureKey]);

  return { showStart, showEnd };
}

export type IconPickerI18n = {
  /** Label for the synthetic "All icons" category. */
  allIconsLabel?: string;
  /** Empty state label when no icons match the search. */
  emptyStateLabel?: string;
  /** Translate raw category keys from `icons-data.ts` (e.g. "communication"). */
  getCategoryLabel?: (categoryKey: string) => string;
  /** Label shown in tooltips. */
  getIconLabel?: (icon: IconData) => string;
};

interface IconPickerProps extends Omit<
  React.ComponentPropsWithoutRef<typeof PopoverTrigger>,
  'onSelect' | 'onOpenChange'
> {
  value?: IconName;
  defaultValue?: IconName;
  onValueChange?: (value: IconName) => void;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  searchable?: boolean;
  searchPlaceholder?: string;
  triggerPlaceholder?: string;
  iconsList?: IconData[];
  categorized?: boolean;
  modal?: boolean;
  i18n?: IconPickerI18n;
}

const IconRenderer = React.memo(({ name }: { name: IconName }) => {
  return (
    <DynamicIcon
      name={name}
      className="h-4 w-4 shrink-0"
      fallback={PickerIconFallback}
    />
  );
});
IconRenderer.displayName = 'IconRenderer';

const IconsColumnSkeleton = () => {
  return (
    <div className="flex flex-col gap-2 w-full">
      <Skeleton className="h-4 w-1/2 rounded-md" />
      <div className="grid grid-cols-5 gap-2 w-full">
        {Array.from({ length: 40 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-10 rounded-md" />
        ))}
      </div>
    </div>
  );
};

const useIconsData = () => {
  const [icons, setIcons] = useState<IconData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadIcons = async () => {
      setIsLoading(true);

      const { iconsData } = await import('./icons-data');
      if (isMounted) {
        setIcons(
          iconsData.filter((icon: IconData) => {
            return icon.name in dynamicIconImports;
          }),
        );
        setIsLoading(false);
      }
    };

    loadIcons();

    return () => {
      isMounted = false;
    };
  }, []);

  return { icons, isLoading };
};

const IconPicker = React.forwardRef<
  React.ComponentRef<typeof PopoverTrigger>,
  IconPickerProps
>(
  (
    {
      value,
      defaultValue,
      onValueChange,
      open,
      defaultOpen,
      onOpenChange,
      children,
      searchable = true,
      searchPlaceholder = 'Search for an icon...',
      triggerPlaceholder = 'Select an icon',
      iconsList,
      categorized = true,
      modal = false,
      i18n,
      ...props
    },
    ref,
  ) => {
    const [selectedIcon, setSelectedIcon] = useState<IconName | undefined>(
      defaultValue,
    );
    const [isOpen, setIsOpen] = useState(defaultOpen || false);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useDebounceValue('', 100);
    const [isPopoverVisible, setIsPopoverVisible] = useState(false);
    const { icons, isLoading: isIconsLoading } = useIconsData();
    const [isMeasuring, setIsMeasuring] = useState(false);

    const iconsToUse = useMemo(() => iconsList || icons, [iconsList, icons]);

    const fuseInstance = useMemo(() => {
      return new Fuse(iconsToUse, {
        keys: ['name', 'tags', 'categories'],
        threshold: 0.3,
        ignoreLocation: true,
        includeScore: true,
      });
    }, [iconsToUse]);

    const filteredIcons = useMemo(() => {
      if (search.trim() === '') {
        return iconsToUse;
      }

      const results = fuseInstance.search(search.toLowerCase().trim());
      return results.map((result) => result.item);
    }, [search, iconsToUse, fuseInstance]);

    const categoryLabel = useCallback(
      (key: string) => i18n?.getCategoryLabel?.(key) ?? key,
      [i18n],
    );

    const iconLabel = useCallback(
      (icon: IconData) => i18n?.getIconLabel?.(icon) ?? icon.name,
      [i18n],
    );

    const categorizedIcons = useMemo(() => {
      if (!categorized || search.trim() !== '') {
        return [
          {
            key: '__all__',
            label: i18n?.allIconsLabel ?? 'All Icons',
            icons: filteredIcons,
          },
        ];
      }

      const categories = new Map<string, IconData[]>();

      filteredIcons.forEach((icon) => {
        if (icon.categories && icon.categories.length > 0) {
          icon.categories.forEach((category) => {
            if (!categories.has(category)) {
              categories.set(category, []);
            }
            categories.get(category)!.push(icon);
          });
        } else {
          const category = 'Other';
          if (!categories.has(category)) {
            categories.set(category, []);
          }
          categories.get(category)!.push(icon);
        }
      });

      return Array.from(categories.entries())
        .map(([key, icons]) => ({
          key,
          label: categoryLabel(key),
          icons,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }, [filteredIcons, categorized, search, categoryLabel, i18n]);

    const virtualItems = useMemo(() => {
      const items: Array<{
        type: 'category' | 'row';
        categoryIndex: number;
        rowIndex?: number;
        icons?: IconData[];
      }> = [];

      categorizedIcons.forEach((category, categoryIndex) => {
        items.push({ type: 'category', categoryIndex });

        const rows = [];
        for (let i = 0; i < category.icons.length; i += 5) {
          rows.push(category.icons.slice(i, i + 5));
        }

        rows.forEach((rowIcons, rowIndex) => {
          items.push({
            type: 'row',
            categoryIndex,
            rowIndex,
            icons: rowIcons,
          });
        });
      });

      return items;
    }, [categorizedIcons]);

    const categoryIndices = useMemo(() => {
      const indices: Record<string, number> = {};

      virtualItems.forEach((item, index) => {
        if (item.type === 'category') {
          const category = categorizedIcons[item.categoryIndex];
          if (category) {
            indices[category.key] = index;
          }
        }
      });

      return indices;
    }, [virtualItems, categorizedIcons]);

    const virtualListRef = useRef<VirtualizedIconListHandle>(null);
    const [iconScrollElement, setIconScrollElement] =
      useState<HTMLDivElement | null>(null);
    const [categoryScrollElement, setCategoryScrollElement] =
      useState<HTMLDivElement | null>(null);
    const categoryScrollElementRef = useRef<HTMLDivElement | null>(null);

    const { showStart: showCategoryFadeStart, showEnd: showCategoryFadeEnd } =
      useScrollFades({
        element: categoryScrollElement,
        axis: 'x',
        threshold: 2,
        measureKey: `${categorizedIcons.length}-${search}`,
      });

    const { showStart: showIconFadeTop, showEnd: showIconFadeBottom } =
      useScrollFades({
        element: iconScrollElement,
        axis: 'y',
        threshold: 2,
        measureKey: `${filteredIcons.length}-${categorized}-${search}`,
      });

    // Desktop: drag the category row to scroll horizontally (keeps buttons clickable).
    const categoryDragRef = React.useRef<{
      isDown: boolean;
      startX: number;
      startScrollLeft: number;
      didDrag: boolean;
      pointerId?: number;
      lastX: number;
      lastT: number;
      velocity: number; // px/ms (scrollLeft direction)
      rafId?: number;
      swallowNextClick: boolean;
    }>({
      isDown: false,
      startX: 0,
      startScrollLeft: 0,
      didDrag: false,
      lastX: 0,
      lastT: 0,
      velocity: 0,
      swallowNextClick: false,
    });

    const stopCategoryInertia = useCallback(() => {
      const state = categoryDragRef.current;
      if (state.rafId != null) {
        cancelAnimationFrame(state.rafId);
        state.rafId = undefined;
      }
      state.velocity = 0;
    }, []);

    const startCategoryInertia = useCallback(() => {
      const el = categoryScrollElementRef.current;
      if (!el) return;

      const state = categoryDragRef.current;
      // Cap velocity to avoid crazy fling.
      let v = Math.max(-2.5, Math.min(2.5, state.velocity));
      if (Math.abs(v) < 0.02) return;

      const friction = 0.0026; // px/ms^2 (tweak)
      let last = performance.now();

      const step = (now: number) => {
        const dt = Math.min(32, now - last);
        last = now;

        // Apply velocity.
        el.scrollLeft += v * dt;

        // Decelerate.
        const decel = friction * dt;
        if (v > 0) v = Math.max(0, v - decel);
        else v = Math.min(0, v + decel);

        // Stop if velocity is tiny or we hit an edge.
        const max = el.scrollWidth - el.clientWidth;
        const atEdge = el.scrollLeft <= 0 || el.scrollLeft >= max;
        if (Math.abs(v) < 0.02 || atEdge) {
          state.rafId = undefined;
          state.velocity = 0;
          return;
        }

        state.rafId = requestAnimationFrame(step);
      };

      stopCategoryInertia();
      state.rafId = requestAnimationFrame(step);
    }, [stopCategoryInertia]);

    const onCategoryPointerDown = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        if (e.pointerType !== 'mouse') return;
        const categoryScrollElement = categoryScrollElementRef.current;
        if (!categoryScrollElement) return;

        stopCategoryInertia();

        categoryDragRef.current.isDown = true;
        categoryDragRef.current.pointerId = e.pointerId;
        categoryDragRef.current.startX = e.clientX;
        categoryDragRef.current.startScrollLeft =
          categoryScrollElement.scrollLeft;
        categoryDragRef.current.didDrag = false;
        categoryDragRef.current.swallowNextClick = false;
        categoryDragRef.current.lastX = e.clientX;
        categoryDragRef.current.lastT = performance.now();
        categoryDragRef.current.velocity = 0;
      },
      [stopCategoryInertia],
    );

    const onCategoryPointerMove = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        if (e.pointerType !== 'mouse') return;
        const categoryScrollElement = categoryScrollElementRef.current;
        if (!categoryScrollElement) return;
        const state = categoryDragRef.current;
        if (!state.isDown) return;

        const deltaX = e.clientX - state.startX;
        if (Math.abs(deltaX) > 10) {
          if (!state.didDrag) {
            state.didDrag = true;
            // Start capturing once we actually drag (keeps clicks working).
            categoryScrollElement.setPointerCapture(e.pointerId);
          }
        }

        if (state.didDrag) {
          // Prevent text selection while dragging.
          e.preventDefault();
          categoryScrollElement.scrollLeft = state.startScrollLeft - deltaX;

          const now = performance.now();
          const dx = e.clientX - state.lastX;
          const dt = Math.max(1, now - state.lastT);
          // scrollLeft moves opposite to pointer movement (because we subtract deltaX).
          state.velocity = -dx / dt;
          state.lastX = e.clientX;
          state.lastT = now;
        }
      },
      [],
    );

    const endCategoryDrag = useCallback((pointerId?: number) => {
      const state = categoryDragRef.current;
      if (pointerId != null && state.pointerId === pointerId) {
        state.pointerId = undefined;
      }
      state.isDown = false;
    }, []);

    const onCategoryPointerUp = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        if (e.pointerType !== 'mouse') return;
        const state = categoryDragRef.current;
        const wasDrag = state.didDrag;
        endCategoryDrag(e.pointerId);
        startCategoryInertia();
        state.swallowNextClick = wasDrag;
        state.didDrag = false;
      },
      [endCategoryDrag, startCategoryInertia],
    );

    const onCategoryPointerCancel = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        if (e.pointerType !== 'mouse') return;
        endCategoryDrag(e.pointerId);
        categoryDragRef.current.didDrag = false;
      },
      [endCategoryDrag],
    );

    const onCategoryClickCapture = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        // Swallow the synthetic click right after dragging so buttons don't trigger.
        if (categoryDragRef.current.swallowNextClick) {
          e.preventDefault();
          e.stopPropagation();
          categoryDragRef.current.swallowNextClick = false;
        }
      },
      [],
    );

    const handleValueChange = useCallback(
      (icon: IconName) => {
        if (value === undefined) {
          setSelectedIcon(icon);
        }
        onValueChange?.(icon);
      },
      [value, onValueChange],
    );

    const handleOpenChange = useCallback(
      (newOpen: boolean) => {
        setSearchInput('');
        setSearch('');
        if (open === undefined) {
          setIsOpen(newOpen);
        }
        onOpenChange?.(newOpen);

        setIsPopoverVisible(newOpen);

        if (newOpen) {
          setIsMeasuring(true);
          requestAnimationFrame(() => {
            virtualListRef.current?.measure();
            setIsMeasuring(false);
          });
        } else {
          setIsMeasuring(false);
        }
      },
      [open, onOpenChange, setSearch],
    );

    const handleIconClick = useCallback(
      (iconName: IconName) => {
        handleValueChange(iconName);
        handleOpenChange(false);
      },
      [handleOpenChange, handleValueChange],
    );

    const handleSearchChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value;
        setSearchInput(v);
        setSearch(v);

        virtualListRef.current?.resetScrollTop();
        virtualListRef.current?.scrollToOffset(0);
      },
      [setSearch],
    );

    const scrollToCategory = useCallback(
      (categoryKey: string) => {
        const categoryIndex = categoryIndices[categoryKey];

        if (categoryIndex !== undefined) {
          virtualListRef.current?.scrollToIndex(categoryIndex, {
            align: 'start',
            behavior: 'smooth',
          });
        }
      },
      [categoryIndices],
    );

    const categoryButtons = useMemo(() => {
      if (!categorized || search.trim() !== '') return null;

      return categorizedIcons.map((category) => (
        <Button
          key={category.key}
          variant={'outline'}
          size="sm"
          className="text-xs"
          onClick={(e) => {
            e.stopPropagation();
            scrollToCategory(category.key);
          }}
        >
          {category.label}
        </Button>
      ));
    }, [categorizedIcons, scrollToCategory, categorized, search]);

    const renderIcon = useCallback(
      (icon: IconData) => {
        const label = iconLabel(icon);
        return (
          <Tooltip key={icon.name}>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={label}
                title={label}
                className={cn(
                  'p-2 rounded-md border hover:bg-foreground/10 transition',
                  'flex items-center justify-center',
                  'touch-pan-y',
                )}
                onClick={() => handleIconClick(icon.name as IconName)}
              >
                <IconRenderer name={icon.name as IconName} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="break-all">{label}</p>
            </TooltipContent>
          </Tooltip>
        );
      },
      [handleIconClick, iconLabel],
    );

    const isLoading = isIconsLoading || isMeasuring;

    return (
      <Popover
        open={open ?? isOpen}
        onOpenChange={handleOpenChange}
        modal={modal}
      >
        <PopoverTrigger ref={ref} asChild {...props}>
          {children || (
            <Button
              variant="outline"
              size="lg"
              className="h-10 min-h-10 w-full justify-start gap-2"
            >
              {value || selectedIcon ? (
                <>
                  <Icon name={(value || selectedIcon)!} />{' '}
                  {value || selectedIcon}
                </>
              ) : (
                triggerPlaceholder
              )}
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent
          className="w-64 p-2"
          onOpenAutoFocus={(e) => {
            // Radix focuses the first focusable element by default (our search input).
            // Prevent this so opening the picker doesn't steal focus / pop the keyboard on mobile.
            e.preventDefault();
          }}
        >
          <TooltipProvider>
            {searchable && (
              <Input
                placeholder={searchPlaceholder}
                value={searchInput}
                onChange={handleSearchChange}
                className="mb-2"
              />
            )}
            {categorized && search.trim() === '' && (
              <div className="relative mt-2">
                <div
                  className={cn(
                    'pointer-events-none absolute inset-y-0 left-0 z-10 w-6 bg-gradient-to-r from-popover to-transparent transition-opacity duration-200',
                    showCategoryFadeStart ? 'opacity-100' : 'opacity-0',
                  )}
                  aria-hidden
                />
                <div
                  className={cn(
                    'pointer-events-none absolute inset-y-0 right-0 z-10 w-6 bg-gradient-to-l from-popover to-transparent transition-opacity duration-200',
                    showCategoryFadeEnd ? 'opacity-100' : 'opacity-0',
                  )}
                  aria-hidden
                />
                <div
                  ref={(node) => {
                    categoryScrollElementRef.current = node;
                    setCategoryScrollElement(node);
                  }}
                  className={cn(
                    'flex flex-row gap-1 overflow-x-auto pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
                    'select-none cursor-grab active:cursor-grabbing',
                  )}
                  onPointerDown={onCategoryPointerDown}
                  onPointerMove={onCategoryPointerMove}
                  onPointerUp={onCategoryPointerUp}
                  onPointerCancel={onCategoryPointerCancel}
                  onClickCapture={onCategoryClickCapture}
                >
                  {categoryButtons}
                </div>
              </div>
            )}
            <div className="relative">
              <div
                className={cn(
                  'pointer-events-none absolute inset-x-0 top-0 z-10 h-8 bg-gradient-to-b from-popover to-transparent transition-opacity duration-200',
                  showIconFadeTop ? 'opacity-100' : 'opacity-0',
                )}
                aria-hidden
              />
              <div
                className={cn(
                  'pointer-events-none absolute inset-x-0 bottom-0 z-10 h-8 bg-gradient-to-t from-popover to-transparent transition-opacity duration-200',
                  showIconFadeBottom ? 'opacity-100' : 'opacity-0',
                )}
                aria-hidden
              />
              {isLoading ? (
                <div className="max-h-60 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                  <IconsColumnSkeleton />
                </div>
              ) : (
                <VirtualizedIconList
                  ref={virtualListRef}
                  virtualItems={virtualItems}
                  categorizedIcons={categorizedIcons}
                  filteredIcons={filteredIcons}
                  renderIcon={renderIcon}
                  emptyStateLabel={i18n?.emptyStateLabel}
                  isVisible={isPopoverVisible}
                  onScrollElementChange={setIconScrollElement}
                />
              )}
            </div>
          </TooltipProvider>
        </PopoverContent>
      </Popover>
    );
  },
);
IconPicker.displayName = 'IconPicker';

interface IconProps extends Omit<LucideProps, 'ref'> {
  name: IconName;
}

const Icon = React.forwardRef<React.ComponentRef<LucideIcon>, IconProps>(
  ({ name, ...props }, ref) => {
    return (
      <DynamicIcon
        name={name}
        {...props}
        ref={ref}
        fallback={PickerIconFallback}
      />
    );
  },
);
Icon.displayName = 'Icon';

export { Icon, type IconName, IconPicker };
